/**
 * 对话框组件
 */

import ReactDOM from 'react-dom';
import cn from 'classnames';
import React, { useState, useEffect, useRef } from 'react';
import TouchableOpacity from '../Touchable/TouchableOpacity';
import Modal from './Modal';
import closeSrc from './images/close.png';
import styles from './styles.module.scss';

type ButtonType = 'default' | 'primary' | 'danger' | 'green' | 'toc';

interface DialogConfig {
  title?: string;
  content: React.ReactNode;
  showCloseBtn: boolean;
  bodyClassNames?: string;
  modalClassNames?: string;
  btns: Array<{
    className?: string;
    type?: ButtonType;
    text: string;
    onClick?(): void;
  }>;
}

let openDialog: (config: DialogConfig) => void | undefined;
let closeDialog: () => void | undefined;

function Dialog() {
  const currentCallbackRef = useRef<() => void>();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [config, setConfig] = useState<DialogConfig>({
    title: '',
    content: '',
    showCloseBtn: false,
    bodyClassNames: '',
    modalClassNames: '',
    btns: [],
  });

  useEffect(() => {
    openDialog = (dConfig: DialogConfig) => {
      setConfig(dConfig);
      setOpen(true);
    };
    closeDialog = () => {
      setOpen(false);
    };
  }, [setConfig, setOpen]);

  return (
    <Modal
      open={isOpen}
      archor="center"
      bodyClassNames={config.bodyClassNames || ''}
      modalClassNames={config.modalClassNames || ''}
      tapMaskClose={false}
      onClose={() => {
        setOpen(false);
      }}
      onClosed={() => {
        if (currentCallbackRef.current) {
          currentCallbackRef.current();
        }
        currentCallbackRef.current = undefined;
      }}
    >
      <div className={styles.dialog}>
        {config.title ? (
          <div className={styles.dialogTitle}>{config.title}</div>
        ) : null}
        <div className={styles.dialogBody}>{config.content}</div>
        <div className={styles.dialogBtns}>
          {config.btns.map((btn, index) => {
            return (
              <TouchableOpacity key={index}>
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={cn(styles.dialogBtn, btn.className, {
                    [styles.primary]: btn.type === 'primary',
                    [styles.danger]: btn.type === 'danger',
                    [styles.green]: btn.type === 'green',
                    [styles.toc]: btn.type === 'toc',
                    [styles.single]: config.btns.length === 1,
                  })}
                  onClick={() => {
                    setOpen(false);
                    currentCallbackRef.current = btn.onClick;
                  }}
                >
                  {btn.text}
                </div>
              </TouchableOpacity>
            );
          })}
        </div>
        {config.showCloseBtn ? (
          <p className={styles.closeIcon} onClick={() => setOpen(false)}>
            <img src={closeSrc} alt="" />
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

if (typeof window !== 'undefined') {
  const div = document.createElement('div');

  div.classList.add('__dialog__');
  document.body.appendChild(div);

  ReactDOM.render(<Dialog />, div);
}

export default {
  show(config: DialogConfig) {
    if (openDialog) {
      openDialog(config);
    }
  },

  alert(config: {
    title?: string;
    content: React.ReactNode;
    showCloseBtn?: boolean;
    bodyClassNames?: string;
    okText?: string;
    okType?: ButtonType;
    onOk?(): void;
  }) {
    if (openDialog) {
      openDialog({
        title: config.title,
        content: config.content,
        showCloseBtn: !!config.showCloseBtn,
        bodyClassNames: config.bodyClassNames,
        btns: [
          {
            type: config.okType || 'primary',
            text: config.okText || '确认',
            onClick: config.onOk,
          },
        ],
      });
    }
  },

  confirm(config: {
    title?: string;
    content: React.ReactNode;
    sureText?: string;
    sureType?: ButtonType;
    showCloseBtn?: boolean;
    bodyClassNames?: string;
    modalClassNames?: string;
    onSure?(): void;
    cancelText?: string;
    cancelType?: ButtonType;
    onCancel?(): void;
  }) {
    if (openDialog) {
      openDialog({
        title: config.title,
        content: config.content,
        showCloseBtn: !!config.showCloseBtn,
        bodyClassNames: config.bodyClassNames,
        modalClassNames: config.modalClassNames,
        btns: [
          {
            type: config.cancelType || 'default',
            text: config.cancelText || '取消',
            onClick: config.onCancel,
          },
          {
            type: config.sureType || 'primary',
            text: config.sureText || '确认',
            onClick: config.onSure,
          },
        ],
      });
    }
  },

  hide() {
    if (closeDialog) {
      closeDialog();
    }
  },
};
